import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Clock, ShieldCheck } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const CommunityApplyPage = () => {
  const { user, loading, isMember, login, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: '', company: '', linkedin: '', city: '', reason: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setError(''); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.company.trim()) { setError('Your role and company are required.'); return; }
    setSubmitting(true); setError('');
    try {
      const res = await fetch(`${API}/api/community/apply`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('failed');
      await checkAuth();
      setDone(true);
    } catch (_) {
      setError('Something went wrong. Please try again.');
    }
    setSubmitting(false);
  };

  const status = user?.status || 'none';

  return (
    <div className="min-h-screen bg-white">
      <SEO title="Apply · Revenue Leaders Community" description="Apply to join the vetted Revenue Leaders Community." path="/community/apply" />
      <Header />
      <main className="pt-20 pb-24">
        <div className="max-w-xl mx-auto px-6">
          <button onClick={() => navigate('/community')} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-8"><ArrowLeft className="w-4 h-4" /> Community</button>

          {loading ? (
            <div className="text-slate-400 text-sm">Loading…</div>
          ) : isMember ? (
            <State icon={CheckCircle2} tone="emerald" title="You are already a member" body="You have full access to the directory and messaging." cta="Go to the community" onCta={() => navigate('/community')} />
          ) : !user ? (
            <div className="text-center">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 mb-4"><ShieldCheck className="w-4 h-4" /> By application only</div>
              <h1 className="text-4xl font-extrabold tracking-tight text-slate-900">Apply to the Revenue Leaders Community</h1>
              <p className="mt-4 text-slate-600 leading-relaxed">Sign in with Google to start your application. We review every applicant by hand.</p>
              <button onClick={login} data-testid="apply-google-login" className="mt-8 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800 transition-colors">
                Continue with Google
              </button>
            </div>
          ) : status === 'pending' && !done ? (
            <State icon={Clock} tone="amber" title="Application under review" body="Thanks — a human reviews every application. You will get an email the moment you are approved." cta="Back to community" onCta={() => navigate('/community')} />
          ) : done ? (
            <State icon={CheckCircle2} tone="emerald" title="Application received" body="You are in the queue. We review by hand and will email you when a decision is made." cta="Back to community" onCta={() => navigate('/community')} />
          ) : (
            <>
              <div className="flex items-center gap-3 mb-6">
                {user.picture ? <img src={user.picture} alt="" className="w-10 h-10 rounded-full" /> : null}
                <div><div className="font-semibold text-slate-900">{user.name}</div><div className="text-sm text-slate-500">{user.email}</div></div>
              </div>
              <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 mb-2">Tell us about you</h1>
              <p className="text-slate-600 mb-6">Two minutes. We only admit genuine revenue leaders.</p>
              <form onSubmit={submit} className="space-y-3" data-testid="apply-form">
                <Field label="Your role / title *" value={form.title} onChange={set('title')} placeholder="e.g. Chief Revenue Officer" testid="apply-title" />
                <Field label="Company *" value={form.company} onChange={set('company')} placeholder="e.g. Acme Inc." testid="apply-company" />
                <Field label="LinkedIn" value={form.linkedin} onChange={set('linkedin')} placeholder="linkedin.com/in/…" testid="apply-linkedin" />
                <Field label="City" value={form.city} onChange={set('city')} placeholder="e.g. Bengaluru" testid="apply-city" />
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">What do you want from the room?</label>
                  <textarea value={form.reason} onChange={set('reason')} rows={3} placeholder="A line or two on what you are working on." data-testid="apply-reason"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/20" />
                </div>
                {error && <p className="text-red-500 text-xs" data-testid="apply-error">{error}</p>}
                <button type="submit" disabled={submitting} data-testid="apply-submit" className="w-full inline-flex items-center justify-center px-6 py-3.5 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800 disabled:opacity-60 transition-colors">
                  {submitting ? 'Submitting…' : 'Submit application'}
                </button>
              </form>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

const Field = ({ label, value, onChange, placeholder, testid }) => (
  <div>
    <label className="text-xs font-semibold text-slate-600 mb-1 block">{label}</label>
    <input value={value} onChange={onChange} placeholder={placeholder} data-testid={testid}
      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/20" />
  </div>
);
const State = ({ icon: Icon, tone, title, body, cta, onCta }) => (
  <div className="text-center py-6" data-testid="apply-state">
    <div className={`mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-4 ${tone === 'amber' ? 'bg-amber-50 border border-amber-200' : 'bg-emerald-50 border border-emerald-200'}`}>
      <Icon className={`w-7 h-7 ${tone === 'amber' ? 'text-amber-600' : 'text-emerald-600'}`} />
    </div>
    <h1 className="text-2xl font-extrabold text-slate-900">{title}</h1>
    <p className="text-slate-600 mt-2 max-w-sm mx-auto">{body}</p>
    <button onClick={onCta} className="mt-6 px-5 py-2.5 rounded-xl bg-slate-100 text-slate-800 text-sm font-semibold hover:bg-slate-200 transition-colors">{cta}</button>
  </div>
);

export default CommunityApplyPage;
